<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchAccountProductsConfig, updateAccountProductsConfig } from '@/api/accountsClient'
import {
  VerifyAdmin,
  getParentAccount,
  moveProductPath,
  fetchProducts,
  fetchParentProducts,
  getParentChild,
  fetchAllProductsFromIndex,
} from '@/api/accountsAdmin'
import { useAccountStore } from '@/stores/accountStore'
import ProgressLoader from '@/components/ProgressLoader.vue'
import ProductHeader from '@/components/ProductHeader.vue'

const route = useRoute()
const router = useRouter()
const accountStore = useAccountStore()
const loading = ref(true)
const productName = ref('')
const productId = route.params.id
const childId = route.query.query
const Enabled = ref(true)
const productType = ref('')

const loader = ref(true)
const errorMessages = ref<string[]>([])
const submitLoader = ref(false)
const dashboardUrl = ref('')

// JSON state helpers
const jsonText = computed(() => JSON.stringify(schema.commonData, null, 2))
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref<'success' | 'error' | 'info' | 'warning'>('success')
const showSnack = (text: string, color: 'success' | 'error' | 'info' | 'warning' = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

// Per-field JSON textarea helpers
const jsonFieldErrors = reactive<Record<string, string | undefined>>({})
const fieldTextMap = reactive<Record<string, string>>({})
const initializeFieldTextMap = () => {
  Object.entries(schema.commonData || {}).forEach(([key, value]) => {
    if (value !== null && typeof value === 'object') {
      fieldTextMap[key] = JSON.stringify(value, null, 2)
    } else {
      fieldTextMap[key] = String(value ?? '')
    }
  })
}
const onJsonFieldChange = (key: string, val: string) => {
  fieldTextMap[key] = val
  try {
    const parsed = JSON.parse(val)
    ;(schema.commonData as any)[key] = parsed
    jsonFieldErrors[key] = undefined
  } catch (e) {
    jsonFieldErrors[key] = 'Invalid JSON'
  }
}

const schema = reactive<{
  productName: string
  config: Record<string, any>
  sampleObj: Record<string, any>
  commonData: Record<string, any>
}>({
  productName: '',
  config: {},
  sampleObj: {},
  commonData: {
    // dashboard: ''
  },
})

// Track initial state for change detection
const initialState = ref<{
  commonData: Record<string, any>
  hierarchy: { productType: 'parent' | 'child'; parentPath: string }
} | null>(null)

// Check if there are any changes
const hasChanges = computed(() => {
  if (!initialState.value) return false

  // Check if commonData has changed
  const currentCommonData = JSON.stringify(schema.commonData)
  const initialCommonData = JSON.stringify(initialState.value.commonData)
  if (currentCommonData !== initialCommonData) return true

  // Check if hierarchy has changed
  if (hierarchy.productType !== initialState.value.hierarchy.productType) return true
  if (hierarchy.parentPath !== initialState.value.hierarchy.parentPath) return true

  return false
})

// Hierarchy editing state
const typeItems = [
  { title: 'Parent', value: 'parent' },
  { title: 'Child', value: 'child' },
] as const
const hierarchy = reactive<{ productType: 'parent' | 'child'; parentPath: string }>({
  productType: 'parent',
  parentPath: '',
})
const parentOptions = ref<any[]>([])
const parentOptionsLoading = ref(false)
const loadParentOptionsFromIndex = async () => {
  try {
    parentOptionsLoading.value = true
    const currentProductId =
      route.params.id === 'child-product'
        ? String(route.query.query || '')
        : String(route.params.id || '')

    console.log('Loading parent options, current product:', currentProductId)

    const response = await fetchAllProductsFromIndex()
    const allProducts = response?.products || []
    parentOptions.value = allProducts
      .filter((p: any) => {
        const productPath = String(p?.path || p?.productId || '')
        const pid = String(p?.productId || '')
        const fullPath = String(p?.fullPath || '')

        // Exclude the current product itself (check all possible identifiers)
        if (
          productPath === currentProductId ||
          pid === currentProductId ||
          fullPath === currentProductId
        ) {
          console.log('Excluding current product:', pid, productPath)
          return false
        }

        // Exclude any descendants of the current product (prevent circular references)
        if (
          productPath.startsWith(currentProductId + '/') ||
          fullPath.startsWith(currentProductId + '/')
        ) {
          console.log('Excluding descendant:', pid, productPath)
          return false
        }

        return true
      })
      .map((p: any) => ({
        ...p,
        path: String(p?.path || p?.productId || ''),
        displayName: `${p?.productFriendlyName || p?.productId || ''} (${p?.path || p?.productId || ''})`,
      }))
      .sort((a: any, b: any) => {
        if (a.level !== b.level) return a.level - b.level
        return (a.productFriendlyName || '').localeCompare(b.productFriendlyName || '')
      })

    console.log('Parent options loaded:', parentOptions.value.length, 'products')
  } catch (e) {
    console.error('Error loading parent options:', e)
    parentOptions.value = []
  } finally {
    parentOptionsLoading.value = false
  }
}

// Watch for product type changes to preselect first parent
watch(
  () => hierarchy.productType,
  (newType, oldType) => {
    // When changing from parent to child, preselect the first parent if none is selected
    if (newType === 'child' && oldType === 'parent' && !hierarchy.parentPath) {
      if (parentOptions.value.length > 0) {
        hierarchy.parentPath = parentOptions.value[0].productId || parentOptions.value[0].path
        console.log('Auto-selected first parent:', hierarchy.parentPath)
      }
    }
    // When changing from child to parent, clear the parent selection
    if (newType === 'parent' && oldType === 'child') {
      hierarchy.parentPath = ''
    }
  }
)

// URL Validation
const validateUrl = (url: string): boolean => {
  const trimmedUrl = url.trim()
  if (!trimmedUrl) {
    errorMessages.value = ['Dashboard URL cannot be empty.']
    return false
  }
  const regex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
  if (!regex.test(trimmedUrl)) {
    errorMessages.value = ['Invalid URL. It must start with http:// or https://']
    return false
  }
  errorMessages.value = []
  return true
}

const loadSchema = async () => {
  try {
    const schemaId =
      route.params.id === 'child-product'
        ? String(route.query.query || '')
        : String(route.params.id || '')
    const data = await fetchAccountProductsConfig(schemaId)
    schema.commonData = data?.commonData
    schema.config = data?.config
    schema.sampleObj = data?.sampleObj
    schema.productName = schemaId

    try {
      const hierarchyResponse = await fetchAllProductsFromIndex()
      const allProducts = hierarchyResponse?.products || []
      const currentProduct = allProducts.find(
        (p: any) => p.productId === schemaId || p.path === schemaId
      )
      if (currentProduct) {
        const hierarchyType = currentProduct.type || 'parent'
        const hierarchyParent = currentProduct.parentPath || ''
        if (hierarchyType === 'child' || hierarchyType === 'intermediate') {
          hierarchy.productType = 'child'
          hierarchy.parentPath = hierarchyParent
        } else {
          hierarchy.productType = 'parent'
          hierarchy.parentPath = ''
        }
        productType.value = hierarchy.productType
      } else {
        if (schemaId.includes('/')) {
          hierarchy.productType = 'child'
          hierarchy.parentPath = schemaId.split('/').slice(0, -1).join('/')
        } else {
          hierarchy.productType = 'parent'
          hierarchy.parentPath = ''
        }
        productType.value = hierarchy.productType
      }
    } catch (error) {
      console.error('Error loading hierarchy from index:', error)
      if (schemaId.includes('/')) {
        hierarchy.productType = 'child'
        hierarchy.parentPath = schemaId.split('/').slice(0, -1).join('/')
      } else {
        hierarchy.productType = 'parent'
        hierarchy.parentPath = ''
      }
      productType.value = hierarchy.productType
    }

    if ((schema.commonData as any).showToCustomers === undefined) {
      ;(schema.commonData as any).showToCustomers = true
    }
    if ((schema.commonData as any).productFriendlyName === undefined) {
      ;(schema.commonData as any).productFriendlyName = ''
    }

    initializeFieldTextMap()

    // Save initial state for change detection
    initialState.value = {
      commonData: JSON.parse(JSON.stringify(schema.commonData)),
      hierarchy: {
        productType: hierarchy.productType,
        parentPath: hierarchy.parentPath,
      },
    }
  } catch (error) {
    console.error('Failed to load schema:', error)
  } finally {
    loader.value = false
  }
}

// Copy JSON content
const copyJson = async (e?: Event) => {
  try {
    e?.preventDefault?.()
    ;(e as any)?.stopPropagation?.()
  } catch {}
  try {
    await navigator.clipboard.writeText(jsonText.value)
    showSnack('Copied to clipboard', 'info')
  } catch (e) {
    console.warn('Clipboard unavailable')
  }
}

// Primitive field change handlers
const onStringChange = (key: string, val: string) => {
  ;(schema.commonData as any)[key] = val
}
const onNumberChange = (key: string, val: string | number) => {
  if (val === '' || val === null || typeof val === 'undefined') {
    ;(schema.commonData as any)[key] = undefined
    return
  }
  const n = typeof val === 'number' ? val : Number(val)
  if (Number.isNaN(n)) {
    ;(schema.commonData as any)[key] = val
  } else {
    ;(schema.commonData as any)[key] = n
  }
}
const onBooleanChange = (key: string, val: boolean) => {
  ;(schema.commonData as any)[key] = val
}

const submitSchema = async () => {
  submitLoader.value = true
  try {
    const configWithoutHierarchy = { ...schema.config }
    delete configWithoutHierarchy.hierarchy
    const payload: any = {
      productName: schema.productName,
      config: configWithoutHierarchy,
      sampleObj: {
        ...schema.sampleObj,
      },
      commonData: {
        ...schema.commonData,
      },
      hierarchy: {
        productType: hierarchy.productType,
        ...(hierarchy.productType === 'child' && hierarchy.parentPath
          ? { parentPath: hierarchy.parentPath }
          : {}),
      },
    }
    await updateAccountProductsConfig(payload)
    showSnack('Product template updated successfully', 'success')
    await new Promise((r) => setTimeout(r, 1200))
    try {
      accountStore.setProductsCache([])
    } catch {}
    goBackToTemplate()
  } catch (error) {
    console.error('Failed to update schema:', error)
    showSnack('Failed to update product template', 'error')
  } finally {
    submitLoader.value = false
  }
}

const goBackToTemplate = () => {
  const isChildNow = hierarchy.productType === 'child'
  const simpleProductName =
    String(schema.productName || '')
      .split('/')
      .pop() || schema.productName
  let newProductPath: string
  if (isChildNow && hierarchy.parentPath) {
    newProductPath = `${hierarchy.parentPath}/${simpleProductName}`
  } else {
    newProductPath = simpleProductName
  }
  if (isChildNow) {
    router.push({
      path: '/products/view/child-product',
      query: { query: newProductPath },
      hash: '#template',
    })
  } else {
    router.push({ path: `/products/view/${newProductPath}`, hash: '#template' })
  }
}

onMounted(async () => {
  await Promise.all([loadSchema(), loadParentOptionsFromIndex()])
})
onMounted(VerifyAdmin)

const goBackToProducts = () => {
  const id = String(route.params.id)
  if (id === 'child-product') {
    router.push({ path: '/products/view/child-product', query: { query: route.query.query } })
  } else {
    router.push(`/products/view/${id}`)
  }
}

const onHeaderSectionNavigate = (s: 'info' | 'clients') => {
  const id = String(route.params.id)
  const hash = s === 'clients' ? '#clients' : '#info'
  if (id === 'child-product') {
    router.push({ path: '/products/view/child-product', query: { query: route.query.query }, hash })
  } else {
    router.push({ path: `/products/view/${id}`, hash })
  }
}
</script>

<template>
  <ProductHeader
    :productId="
      String(
        route.params.id === 'child-product' ? String(route.query.query) : String(route.params.id)
      )
    "
    section="info"
    @update:section="onHeaderSectionNavigate"
  />
  <v-container>
    <div cols="12" class="subheader-sticky">
      <div
        style="
          display: flex !important;
          justify-content: space-between;
          flex-wrap: wrap;
          align-items: center;
        "
      >
        <div class="d-flex align-center" style="gap: 8px">
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-arrow-left"
            class="text-grey-darken-2"
            @click="goBackToProducts"
          >
            Back
          </v-btn>
          <h2 class="text-h5 font-weight-medium" style="margin: 0">Edit Product Template</h2>
        </div>
      </div>
    </div>

    <template v-if="!loader">
      <v-card class="mt-5 pl-5 pr-5 pb-8">
        <v-card-title class="d-flex align-center">
          Edit Template Fields
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-content-copy"
            class="text-grey-darken-2"
            @click="copyJson($event)"
          >
            Copy full JSON
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row class="mb-6">
            <v-col cols="12" md="4" class="text-caption text-grey-darken-1">Product type</v-col>
            <v-col cols="12" md="8">
              <v-select
                :items="typeItems"
                item-title="title"
                item-value="value"
                v-model="hierarchy.productType"
                label="Product type"
                density="comfortable"
                variant="outlined"
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-row class="mb-6" v-if="hierarchy.productType === 'child'">
            <v-col cols="12" md="4" class="text-caption text-grey-darken-1">Parent product</v-col>
            <v-col cols="12" md="8">
              <v-autocomplete
                v-model="hierarchy.parentPath"
                :items="parentOptions"
                :loading="parentOptionsLoading"
                item-title="displayName"
                item-value="productId"
                label="Select parent product"
                density="comfortable"
                variant="outlined"
                hide-details="auto"
                :hint="'Select any product to be the parent (supports multi-level hierarchy)'"
                persistent-hint
              />
            </v-col>
          </v-row>
          <v-row class="mt-1" v-for="(val, key) in schema.commonData" :key="String(key)">
            <v-col
              cols="12"
              md="4"
              class="text-caption text-grey-darken-1"
              style="text-transform: capitalize"
            >
              {{ String(key).split('_').join(' ') }}
            </v-col>
            <v-col cols="12" md="8">
              <template v-if="val === true || val === false">
                <v-switch
                  :model-value="val"
                  @update:modelValue="(v: any) => onBooleanChange(String(key), Boolean(v))"
                  inset
                  color="primary"
                  hide-details="auto"
                />
              </template>
              <template v-else-if="typeof val === 'number'">
                <v-text-field
                  :model-value="val"
                  @update:modelValue="(v: string | number) => onNumberChange(String(key), v)"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                />
              </template>
              <template v-else-if="val === null || typeof val === 'object'">
                <v-textarea
                  :model-value="fieldTextMap[String(key)]"
                  @update:modelValue="(v: string) => onJsonFieldChange(String(key), v)"
                  label="JSON value"
                  auto-grow
                  variant="outlined"
                  density="comfortable"
                  :error-messages="jsonFieldErrors[String(key)]"
                  hide-details="auto"
                  rows="8"
                />
              </template>
              <template v-else>
                <v-text-field
                  :model-value="String(val ?? '')"
                  @update:modelValue="(v: string) => onStringChange(String(key), v)"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                />
              </template>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-fade-transition>
        <div class="floating-footer">
          <v-container class="py-2 d-flex justify-end ga-2">
            <v-btn variant="tonal" color="grey" @click="goBackToTemplate">Cancel</v-btn>
            <v-btn
              color="primary"
              @click="submitSchema"
              :loading="submitLoader"
              :disabled="submitLoader || !hasChanges"
            >
              {{ submitLoader ? 'Updating Product Template' : 'Update Product Template' }}
            </v-btn>
          </v-container>
        </div>
      </v-fade-transition>
    </template>

    <div v-else class="center">
      <progress-loader />
    </div>
  </v-container>
  <v-snackbar v-model="snackbar" :color="snackbarColor" location="bottom center" timeout="2000">
    {{ snackbarText }}
  </v-snackbar>
</template>

<style scoped>
.center {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
}
.schema-editor {
  max-width: 1200px;
  margin: 0 auto;
}
.div-circular {
  border: 2px solid red;
}
.schema-preview {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: monospace;
}
.floating-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
}
.subheader-sticky {
  position: sticky;
  top: 64px;
  z-index: 3;
  background: white;
}
</style>
